import { ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Dropdown } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Pagination } from '@mui/material';

import {
  ExternalOrderModel,
  ExternalOrderStateEnum,
} from '../models/ExternalOrderModel';
import {
  BulkActionsEnum,
  ORDERS_PER_PAGE,
} from '../services/ExternalOrderCRUD';

export interface BulkActionConfig {
  action: BulkActionsEnum;
  i18nKey: string;
  icon: string;
}

const SUCCESS_STATES = [ExternalOrderStateEnum.PROCESSED];
const FAIL_STATES = [
  ExternalOrderStateEnum.ERROR,
  ExternalOrderStateEnum.CANCELLED,
];
const WARNING_STATES = [
  ExternalOrderStateEnum.PROCESSING,
  ExternalOrderStateEnum.RECEIVED,
];

type BULK_STATE = 'loading' | 'idle';

type PropType = {
  bulkActions?: BulkActionConfig[];
  onBulkAction?: (
    action: BulkActionsEnum,
    orderIds: Array<number>
  ) => Promise<unknown>;
  orders: Array<ExternalOrderModel>;
  page: number;
  count: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  children?: ReactNode;
  displayBulkActions?: boolean;
};

export default function ExternalOrderList({
  bulkActions = [],
  onBulkAction,
  onPageChange,
  orders,
  page,
  count,
  loading,
  children,
  displayBulkActions = false,
}: PropType) {
  const intl = useIntl();
  const [selectedOrders, setSelectedOrders] = useState<
    Array<ExternalOrderModel>
  >([]);
  const [bulkState, setBulkState] = useState<BULK_STATE>('idle');
  const hasSelectedAllOrders = selectedOrders?.length === orders?.length;
  const selectedCount = hasSelectedAllOrders ? count : selectedOrders.length;

  useEffect(() => {
    setSelectedOrders([]);
  }, [orders]);

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  async function handleBulkAction(config: BulkActionConfig) {
    const i18nKey = capitalize(config.i18nKey);
    const hasConfirmed = window.confirm(
      intl.formatMessage(
        { id: `externalOrders.bulk${i18nKey}Confirmation` },
        { count: selectedCount }
      )
    );
    if (!hasConfirmed) {
      return;
    }
    setBulkState('loading');
    try {
      const orderIds = hasSelectedAllOrders
        ? []
        : selectedOrders?.map((order) => order.id);
      await onBulkAction?.(config.action, orderIds);
      toast.success(
        intl.formatMessage(
          { id: `externalOrders.bulk${i18nKey}Success` },
          { count: selectedCount }
        )
      );
    } catch (error) {
      console.warn({ error });
      toast.error(
        intl.formatMessage({ id: `externalOrders.bulk${i18nKey}Error` })
      );
    } finally {
      setBulkState('idle');
    }
  }

  return (
    <>
      <div className="card p-10 h-auto">
        <div className="d-flex align-items-center justify-content-between mb-8">
          <h2 className="card-title">
            {intl.formatMessage({ id: 'externalOrders.title' })}
          </h2>
        </div>
        <div className="row mb-4">{children}</div>
        {loading && (
          <div className="spinner-border text-primary" role="status" />
        )}

        {!loading && displayBulkActions && (
          <section className="d-flex px-0 gap-4 align-items-end mb-8">
            <div className="me-4">
              <label className={clsx('mb-2', !selectedCount && 'text-muted')}>
                {selectedCount > 0 ? (
                  <>
                    {intl.formatMessage(
                      { id: 'externalOrders.countSelectedOrders' },
                      { count: selectedCount }
                    )}
                  </>
                ) : (
                  <>
                    {intl.formatMessage({
                      id: 'externalOrders.noSelectedOrders',
                    })}
                  </>
                )}
              </label>
              <Dropdown>
                <Dropdown.Toggle variant="primary" disabled={selectedCount < 1}>
                  <span className="me-4 fas w-15px d-inline-block fa-layer-group" />
                  {intl.formatMessage({ id: 'externalOrders.bulkActions' })}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {bulkActions.map((config) => (
                    <Dropdown.Item
                      key={config.action}
                      className="py-3 ps-5 fs-6"
                      onClick={() => handleBulkAction(config)}
                    >
                      <span
                        className={`me-4 fas w-15px d-inline-block ${config.icon} text-muted`}
                      />
                      {intl.formatMessage({
                        id: `externalOrders.${config.i18nKey}`,
                      })}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </section>
        )}
        {!loading && orders.length === 0 && (
          <p className="fw-bolder">
            {intl.formatMessage({ id: 'externalOrders.noOrders' })}
          </p>
        )}
        {!loading && orders.length > 0 && (
          <div
            className="table-responsive"
            style={{
              height: 'calc(100vh - 420px)',
            }}
          >
            <p className="text-muted mb-4">
              {intl.formatMessage(
                { id: 'externalOrders.countOrders' },
                { visible: orders.length, count }
              )}
            </p>
            <table className="table table-hover table-row-dashed table-row-gray-200 align-start gs-0 gy-4">
              <thead className="table-header sticky-top bg-white">
                <tr className="fw-bolder">
                  {displayBulkActions && (
                    <th className="text-center ps-6 pe-4">
                      <input
                        type="checkbox"
                        className="cursor-pointer form-check w-25px h-25px"
                        disabled={bulkState === 'loading'}
                        checked={selectedOrders.length === orders.length}
                        onChange={() => {
                          if (selectedOrders.length === orders.length) {
                            setSelectedOrders([]);
                          } else {
                            setSelectedOrders(orders);
                          }
                        }}
                      />
                    </th>
                  )}
                  <th>
                    {intl.formatMessage({ id: 'externalOrders.orderNumber' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'externalOrders.status' })}</th>
                  <th>
                    {intl.formatMessage({ id: 'externalOrders.orderDate' })}
                  </th>
                  <th>
                    {intl.formatMessage({
                      id: 'externalOrders.shippingAddress',
                    })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'externalOrders.provider' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'externalOrders.lines' })}</th>
                  <th>
                    {intl.formatMessage({
                      id: 'externalOrders.shipmentNumber',
                    })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'externalOrders.shipmentDate' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'externalOrders.shippingLabel' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders
                  ?.sort((a, b) => b.id - a.id)
                  ?.map((order) => (
                    <tr key={order.id}>
                      {displayBulkActions && (
                        <td className="text-center ps-6 pe-4">
                          <input
                            type="checkbox"
                            className="cursor-pointer form-check w-25px h-25px"
                            checked={selectedOrders.includes(order)}
                            disabled={bulkState === 'loading'}
                            onChange={() => {
                              if (selectedOrders.includes(order)) {
                                setSelectedOrders(
                                  selectedOrders.filter(
                                    (t) => t.id !== order.id
                                  )
                                );
                              } else {
                                setSelectedOrders([...selectedOrders, order]);
                              }
                            }}
                          />
                        </td>
                      )}
                      <td className="text-nowrap">{order.number}</td>
                      <td>
                        <div
                          style={{ width: 'fit-content' }}
                          className={clsx({
                            'badge badge-sm px-4 py-2 d-block': true,
                            'badge-success': SUCCESS_STATES.includes(
                              order.status
                            ),
                            'badge-danger': FAIL_STATES.includes(order.status),
                            'badge-warning text-dark': WARNING_STATES.includes(
                              order.status
                            ),
                          })}
                        >
                          {order.status_display}
                        </div>
                      </td>
                      <td>
                        {order.date
                          ? intl.formatDate(new Date(order.date), {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '-'}
                      </td>
                      <td>
                        <div className="fs-7">
                          <div className="fw-bold">
                            {order.shipping_address?.name}
                          </div>
                          <div>{order.shipping_address?.street}</div>
                          <div>
                            {order.shipping_address?.postcode}{' '}
                            {order.shipping_address?.city}
                          </div>
                          {order.shipping_address?.phone && (
                            <div className="text-muted">
                              {order.shipping_address.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{order.provider?.name || '-'}</td>
                      <td>
                        <ul className="list-unstyled mb-0">
                          {order.lines?.map((line, idx) => (
                            <li key={idx} className="text-nowrap mb-1 fs-7">
                              {line.quantity} &times; {line.item_description}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="text-nowrap">
                        {order.shipment_number || '-'}
                      </td>
                      <td>
                        {order.shipment_date
                          ? intl.formatDate(new Date(order.shipment_date), {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '-'}
                      </td>
                      <td>
                        {order.label_urls?.length > 0 ? (
                          <Dropdown>
                            <Dropdown.Toggle variant="secondary" size="sm">
                              <span className="fa fa-barcode me-2" />
                              {intl.formatMessage({
                                id: 'externalOrders.seeLabels',
                              })}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {order.label_urls.map((url, index) => (
                                <Dropdown.Item
                                  key={url}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <span className="fa fa-external-link-alt me-3" />
                                  {intl.formatMessage(
                                    { id: 'externalOrders.labelNumber' },
                                    { number: index + 1 }
                                  )}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        ) : (
                          <span className="text-muted">
                            {intl.formatMessage({
                              id: 'externalOrders.noLabels',
                            })}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {count > 0 && (
              <Pagination
                className="mx-auto mt-4 d-flex justify-content-center"
                count={Math.ceil(count / ORDERS_PER_PAGE)}
                page={page}
                onChange={(_, page) => onPageChange(page)}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
